import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import {
  ArrowRightIcon,
  EnvelopeIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useBranding } from '../../context/BrandingContext';
import { customerPortalAPI } from '../../services/api';

const customerAccessHeroImage = '/login-hero-luxury-fleet.png';

function storageKey() {
  const host = typeof window !== 'undefined' ? window.location.host : 'local';
  return `modal_customer_portal:${host}`;
}

function savePortalSession(session: {
  portalToken: string;
  email: string;
  expiresAt: string;
}) {
  localStorage.setItem(storageKey(), JSON.stringify(session));
}

export default function CustomerAccessPage() {
  const branding = useBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [magicStatus, setMagicStatus] = useState<'idle' | 'verifying' | 'failed'>('idle');

  const companyName = branding.app_name || 'ModalOS';
  const primaryColor = branding.primary_color || '#2563eb';

  const startMutation = useMutation(
    async () => {
      const response = await customerPortalAPI.startOtp(email);
      return response.data;
    },
    {
      onSuccess: (data) => {
        setChallengeId(data.challengeId || null);
        toast.success('Check your email for the code.');
      },
      onError: () => {
        toast.error('We could not start access right now.');
      },
    }
  );

  const verifyMutation = useMutation(
    async (payload: { challengeId?: string; code?: string; token?: string }) => {
      const response = await customerPortalAPI.verifyOtp(payload);
      return response.data;
    },
    {
      onSuccess: (session) => {
        savePortalSession(session);
        navigate('/customer', { replace: true });
      },
      onError: () => {
        setMagicStatus('failed');
        toast.error('That access code has expired or is incorrect.');
      },
    }
  );

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token || magicStatus !== 'idle') return;
    setMagicStatus('verifying');
    verifyMutation.mutate({ token });
  }, [magicStatus, searchParams, verifyMutation]);

  const sanitizedCode = useMemo(() => code.replace(/\D/g, '').slice(0, 6), [code]);

  function submitEmail(event: React.FormEvent) {
    event.preventDefault();
    if (!email.trim()) return;
    startMutation.mutate();
  }

  function submitCode(event: React.FormEvent) {
    event.preventDefault();
    if (!challengeId || sanitizedCode.length !== 6) return;
    verifyMutation.mutate({ challengeId, code: sanitizedCode });
  }

  if (magicStatus === 'verifying') {
    return (
      <main className="min-h-screen bg-[#101724] px-4 py-10 text-white">
        <div className="mx-auto flex min-h-[70vh] max-w-md items-center justify-center">
          <div className="w-full rounded-card border border-white/15 bg-white/10 p-8 text-center shadow-card backdrop-blur">
            <LoadingSpinner size="lg" />
            <h1 className="mt-5 text-2xl font-semibold">Opening your records</h1>
            <p className="mt-2 text-sm text-white/70">
              Verifying your secure email link for {companyName}.
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#101724] text-white">
      <div className="grid min-h-screen lg:grid-cols-2">
        <section className="relative flex min-h-[42vh] overflow-hidden lg:min-h-screen">
          <img
            src={customerAccessHeroImage}
            alt="Luxury vehicles parked outside a modern hotel"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[#101724]/42" />

          <div className="relative z-10 flex w-full p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-4">
              {branding.logo_url ? (
                <img
                  src={branding.logo_url}
                  alt={companyName}
                  className="h-10 w-auto max-w-[8.5rem] rounded-sm bg-white/95 object-contain p-2 shadow-card"
                />
              ) : (
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-md text-sm font-semibold text-white shadow-card"
                  style={{ backgroundColor: primaryColor }}
                >
                  {companyName.slice(0, 1)}
                </div>
              )}
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase text-white/60">
                  Private Customer Access
                </p>
                <h1 className="truncate text-lg font-semibold sm:text-xl">
                  {companyName}
                </h1>
              </div>
            </div>
          </div>
        </section>

        <section className="flex items-center bg-surface px-4 py-10 text-on-surface sm:px-10 lg:px-14">
          <div className="mx-auto w-full max-w-lg">
            <div className="mb-9">
              <h2 className="text-4xl font-semibold leading-tight text-on-surface sm:text-5xl">
                Access your records
              </h2>
              <p className="mt-4 max-w-md text-base leading-7 text-on-surface-variant">
                Enter your email and we will send a secure code to open your quotes, reservations, invoices, and payment records.
              </p>
            </div>

            <div className="rounded-card border border-outline/60 bg-surface-container-lowest p-6 shadow-card sm:p-8">
              {!challengeId ? (
                <form onSubmit={submitEmail}>
                  <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <EnvelopeIcon className="h-5 w-5" />
                    </div>
                    <p className="text-sm leading-5 text-on-surface-variant">
                      Use the email address associated with your trip or billing record.
                    </p>
                  </div>
                  <label className="mt-6 block text-sm font-medium text-on-surface">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    autoComplete="email"
                    className="mt-2 w-full rounded-card border border-outline bg-surface px-4 py-3 text-base text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="name@example.com"
                    required
                  />
                  <button
                    type="submit"
                    disabled={startMutation.isLoading || !email.trim()}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {startMutation.isLoading ? <LoadingSpinner size="sm" /> : <ArrowRightIcon className="h-4 w-4" />}
                    Send secure code
                  </button>
                  <p className="mt-4 text-center text-xs leading-5 text-on-surface-variant">
                    Access is limited to records matching this exact email.
                  </p>
                </form>
              ) : (
                <form onSubmit={submitCode}>
                  <div className="mb-6 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                      <KeyIcon className="h-5 w-5" />
                    </div>
                    <p className="text-sm leading-5 text-on-surface-variant">
                      Enter the six digit code, or open the secure link from the email.
                    </p>
                  </div>
                  <label className="mt-6 block text-sm font-medium text-on-surface">
                    Six digit code
                  </label>
                  <input
                    value={sanitizedCode}
                    onChange={(event) => setCode(event.target.value)}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="mt-2 w-full rounded-card border border-outline bg-surface px-4 py-3 text-center text-2xl font-semibold tracking-[0.35em] text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                    placeholder="000000"
                    required
                  />
                  <button
                    type="submit"
                    disabled={verifyMutation.isLoading || sanitizedCode.length !== 6}
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {verifyMutation.isLoading ? <LoadingSpinner size="sm" /> : <ArrowRightIcon className="h-4 w-4" />}
                    Open customer center
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setChallengeId(null);
                      setCode('');
                    }}
                    className="mt-3 w-full rounded-full border border-outline px-5 py-3 text-sm font-semibold text-on-surface"
                  >
                    Use a different email
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
